import React, { useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faUndo } from '@fortawesome/free-solid-svg-icons';
import GlSegmentSelect from './gl-segment-select';
import HoverInfoCard from './hover-card';
import {
    FACILITY_PICKER_MIN_WIDTH,
    isSegmentOverridden,
    segmentRuleOf,
    withSegmentChoice,
    withSegmentRule,
} from '../utils/gl-segment-rules';

// XPEN-3746: a line's GL segment governed by the firm's rules. A value the rules leave to
// the user carries the halt icon a missing Activity does; a value the user picked can be
// handed back to the rules. `onChange` receives the whole updated line.
const SegmentRuleField = ({ item, segment, data, onChange, isDisabled, label }) => {
    const pickerRef = useRef(null);
    const rule = segmentRuleOf(item, segment);
    const overridden = isSegmentOverridden(item, segment);
    const value = item?.[segment];
    const isEmpty = value === undefined || value === null || value === '' || (typeof value === 'object' && !value.code);
    const mustChoose = Boolean(rule?.must_choose) && isEmpty && !overridden;
    const read = (key, fallback) => data?.t?.read(key) || fallback;
    const showHalt = useCallback((callback) => callback(mustChoose), [mustChoose]);

    const openPicker = () => {
        const input = pickerRef.current?.querySelector('input');
        if (!input || isDisabled) return;
        input.focus();
        input.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true }));
    };

    return (
        <div className="d-flex align-items-center w-100" style={{ gap: '6px' }} data-testid={`segment-rule-field-${segment}`}>
            {/* The picker fills its column and never shrinks below a readable code; the
                icons beside it never shrink. */}
            <div ref={pickerRef} className="flex-grow-1" style={{ minWidth: FACILITY_PICKER_MIN_WIDTH }}
                data-testid={`segment-rule-picker-${segment}`}>
                <GlSegmentSelect
                    segment={segment}
                    value={value}
                    ariaLabel={label}
                    placeholder={mustChoose ? read('segment_rules.choose_placeholder', 'Choose…') : label}
                    isDisabled={isDisabled}
                    allowedCodes={mustChoose && rule?.options?.length ? rule.options : null}
                    compact
                    onChange={(picked) => onChange(withSegmentChoice(item, segment, picked))}
                />
            </div>
            {mustChoose && (
                <div onClick={openPicker} className="flex-shrink-0" data-testid={`segment-rule-required-${segment}`}>
                    <HoverInfoCard
                        info={<FontAwesomeIcon icon={faExclamationTriangle} className='text-primary pointer' size={'2x'} />}
                        position="pos-top-right"
                        additionalInfo={
                            <div style={{ minWidth: '200px', textTransform: 'none' }}>
                                <strong className="mr-2">{read('validation_error', 'Validation error')} </strong>
                                <strong>{label}</strong>
                                <div>
                                    <p className="mb-0 mt-2 italic">
                                        {read('click_to', 'Click to')} {read('select', 'select')} {label}
                                    </p>
                                </div>
                            </div>
                        }
                        shouldDisplayIcon={showHalt}
                    />
                </div>
            )}
            {overridden && (
                <button type="button" className="btn btn-link btn-sm p-0 flex-shrink-0" disabled={isDisabled}
                    title={`${read('segment_rules.line_changed_by_you', 'Changed by you')} — ${read('segment_rules.line_use_rule', 'Use the rule')}`}
                    aria-label={read('segment_rules.line_use_rule', 'Use the rule')}
                    onClick={() => onChange(withSegmentRule(item, segment))}>
                    <FontAwesomeIcon icon={faUndo} />
                </button>
            )}
        </div>
    );
};

export default SegmentRuleField;
